import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Spinner from "react-bootstrap/Spinner";
import { Badge } from "react-bootstrap";
import DatePicker from "react-datepicker";

import { useDispatch, useMappedState } from "redux-react-hook";
import moment from "moment";
import EnumDropdown from "../EnumDropdown";
import {
  BillingFrequency,
  BillingFrequencyDisplayName,
  SubscriptionStatus,
  SubscriptionStatusDisplayName,
} from "../../types/subscriptionTypes";
import {
  getCorrectFormat,
  getDateAtTimeFormat,
  getDateAtTimeFormatString,
  getNextBillingDateFormat,
} from "../../utilities/date";
import OrderDetailsTable from "../OrderDetailsTable";
import { subscriptionConstants } from "../../constants/subscriptionConstants";
import CustomerInfoPane from "../CustomerInfoPane";
import CancelSubscriptionModal from "../Modals/CancelSubscriptionModal";
import DownsellModal from "../Modals/DownsellModal";
import OrderHistory from "../OrderHistory";
import SubscriptionDetailsTable from "../SubscriptionDetailsTable";
import { getTaxRatePercent } from "../../utilities/taxRate";
import ExpireModal from "../Modals/ExpireModal";
import AdvancedEditingBlock from "../AdvancedEditingBlock";

export const SubscriptionInfoBlock = ({
  isSaving,
  isEditing,
  setIsEditing,
  initialSubscription,
  setInitialSubscription,
  saveRow,
  onCancelSubscription,
  setCancelMessage,
  setShowCancelModal,
  setShowRestartModal,
  setShowDownsellModal,
  cancelReason,
  setCancelReason,
}) => {
  const dispatch = useDispatch();

  const subscription = useMappedState((state) => state.subscriptions.subscription);
  const history = useMappedState((state) => state.subscriptions.history);
  const pendingOrder = useMappedState((state) => state.subscriptions.pendingOrder);
  const isLoadingSubOrders = useMappedState((state) => state.loading.isLoadingSubOrders);
  const [showAllHistory, setShowAllHistory] = useState(false);
  const [isCanChange, setIsCanChange] = useState(true);
  const [lastSubscriptionsDay, setLastSubscriptionsDay] = useState<string>();
  const [showExpireModal, setShowExpireModal] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const {
    signupDate,
    previousBillingDate,
    billing,
    billingFrequency,
    status,
    shippingAddress,
    downsell_discount_is_available,
    taxRate,
    priceTag,
  } = subscription;

  const isCancelled = status.status === SubscriptionStatus.CANCELED;

  const getSpecialPlanBadgeText = () => {
    if (!priceTag || !priceTag.name) return null;

    const rawName = String(priceTag.name);
    const normalizedName = rawName.toLowerCase();

    let months: string | null = null;
    if (normalizedName.includes("3x")) months = "3";
    else if (normalizedName.includes("6x")) months = "6";

    if (months) return `Special ${months}-Month pricing plan (${rawName}), do NOT modify subscription pricing`;

    return rawName;
  };
  const specialPlanBadgeText = getSpecialPlanBadgeText();

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const isDevMode = queryParams.get("devmode") === "true";

  const taxRatePercent = getTaxRatePercent(taxRate);

  useEffect(() => {
    if (isCanChange) {
      const lastDay = moment(getCorrectFormat(billing?.nextBillingDate))
        .add(6, "M")
        .endOf("month")
        .endOf("day")
        .format("YYYY-MM-DD HH:mm:ss");
      setLastSubscriptionsDay(lastDay);
    }
  }, [billing?.nextBillingDate]);

  const handleStatusChange = (value: SubscriptionStatus) => {
    if (value === SubscriptionStatus.CANCELED) {
      setShowCancelModal(true);
    } else if (value === SubscriptionStatus.ACTIVE) {
      setShowRestartModal(true);
    } else if (value === SubscriptionStatus.EXPIRED) {
      setShowExpireModal(true);
    } else {
      dispatch({
        type: subscriptionConstants.UPDATE_SUBSCRIPTION_STATUS,
        payload: {
          status: value,
        },
      });
    }
  };

  const handleFrequencyChange = (value: BillingFrequency) => {
    const frequency = value.split(":");
    const copyDate = new Date(subscription.previousBillingDate || subscription.signupDate);

    if (frequency[1] === "month") {
      copyDate.setMonth(copyDate.getMonth() + Number(frequency[0]));
    } else {
      copyDate.setDate(copyDate.getDate() + Number(frequency[0]) * 7);
    }

    const currentDate = new Date();

    if (copyDate < currentDate) {
      copyDate.setDate(currentDate.getDate() + 1);
    }
    dispatch({
      type: subscriptionConstants.UPDATE_SUBSCRIPTION_BILLING_FREQUENCY,
      payload: {
        frequency: value,
        nextBillingDate: getNextBillingDateFormat(copyDate),
      },
    });
  };

  const getBillingFrequencyKey = useCallback((frequency) => {
    if (!frequency?.schedule_type) return frequency;
    return `${frequency.schedule_value}:${frequency.schedule_type}`;
  }, []);

  const toggleShowAllHistory = () => {
    setShowAllHistory(!showAllHistory);
    if (panelRef.current) {
      panelRef.current.scrollTop = 0;
    }
  };

  const tomorrow = moment()
    .add(1, "days")
    .startOf("day")
    .format("YYYY-MM-DD HH:mm:ss");

  const yearFromToday = moment().add(1, "year").startOf("day").format("YYYY-MM-DD HH:mm:ss");

  const handleNextDateChange = (value: Date) => {
    setIsCanChange(false);
    dispatch({
      type: subscriptionConstants.UPDATE_SUBSCRIPTION_STATUS,
      payload: {
        status: moment(value).isAfter(lastSubscriptionsDay) ? SubscriptionStatus.PAUSED : SubscriptionStatus.ACTIVE,
      },
    });

    dispatch({
      type: subscriptionConstants.UPDATE_SUBSCRIPTION_BILLING_DATE,
      payload: {
        nextBillingDate: getCorrectFormat(value.getTime()),
      },
    });
  };

  const generateNewDate = useCallback((param) => new Date(param), []);

  const getSelectedDate = (billingDate) => {
    const formattedDate = getCorrectFormat(billingDate);
    if (!formattedDate) return null;
    return new Date(formattedDate);
  };

  const onHideModal = () => {
    setIsEditing(false);
    setShowExpireModal(false);
  };

  return (
    <>
      <Container fluid className="flex-grow-1 overflow-hidden">
        <Row className="h-100">
          <Col className="p-5 h-100 overflow-auto bg-white" ref={panelRef}>
            <Row>
              <Col xl={3}>
                <h6 className="m-0">
                  <small className="font-weight-bold">Subscription Sign Up Date</small>
                </h6>
                <p>{signupDate}</p>
              </Col>
              <Col xl={3}>
                <h6 className="m-0">
                  <small className="font-weight-bold">Previous Billing Date</small>
                </h6>
                <p>{previousBillingDate || signupDate}</p>
              </Col>
              <Col xl={2}>
                <h6 className="m-0">
                  <small className="font-weight-bold">Status</small>
                </h6>
                {isEditing && !isSaving ? (
                  <EnumDropdown
                    enumerable={SubscriptionStatus}
                    enumDisplayName={SubscriptionStatusDisplayName}
                    value={status.status}
                    setValue={(value) => handleStatusChange(value)}
                    isDevMode={isDevMode}
                  />
                ) : (
                  <p>{SubscriptionStatusDisplayName.get(status.status)}</p>
                )}
              </Col>
              {!isDevMode && (
                <Col xl={2}>
                  <h6 className="m-0">
                    <small className="font-weight-bold">Tax Rate</small>
                  </h6>
                  <p>{taxRatePercent ? `${taxRatePercent}%` : "0%"}</p>
                </Col>
              )}
              <Col>
                <div className="d-flex flex-column flex-md-row justify-content-xl-end mb-4" style={{ gap: "10px" }}>
                  {downsell_discount_is_available && (
                    <DownsellModal setShowDownsellModal={setShowDownsellModal} isCancelled={isCancelled} />
                  )}
                  {isCancelled && (
                    <CancelSubscriptionModal
                      subscriptionId={subscription}
                      setCancelReason={setCancelReason}
                      setCancelMessage={setCancelMessage}
                      cancelSubscription={onCancelSubscription}
                      cancelReason={cancelReason}
                      buttonText="Change Cancel Reason"
                    />
                  )}
                </div>
              </Col>
            </Row>
            <Row>
              <Col>
                <h6 className="m-0">
                  <small className="font-weight-bold">Billing Frequency</small>
                </h6>
                {isEditing && !isSaving ? (
                  <EnumDropdown
                    enumerable={BillingFrequency}
                    enumDisplayName={BillingFrequencyDisplayName}
                    value={getBillingFrequencyKey(billingFrequency)}
                    setValue={(value) => handleFrequencyChange(value)}
                  />
                ) : (
                  <p>{BillingFrequencyDisplayName.get(getBillingFrequencyKey(billingFrequency))}</p>
                )}
              </Col>
              <Col>
                <h6 className="m-0">
                  <small className="font-weight-bold">Next Billing Date</small>
                </h6>
                {isEditing && !isSaving ? (
                  <DatePicker
                    showTimeSelect
                    minDate={generateNewDate(tomorrow)}
                    maxDate={generateNewDate(yearFromToday)}
                    selected={getSelectedDate(billing?.nextBillingDate)}
                    onChange={(choiceDate: Date) => handleNextDateChange(choiceDate)}
                    dateFormat="MM/dd/yyyy h:mm aa"
                  />
                ) : (
                  <p>{getDateAtTimeFormat(billing?.nextBillingDate)}</p>
                )}
              </Col>
              <Col className="d-none d-xl-block">
                {
                  status.cancelMessage ?
                    <>
                      <h6 className="m-0">
                        <small className="font-weight-bold">Cancellation Reason</small>
                      </h6>
                      <p>{status.cancelMessage}</p>
                    </> :
                    <p />
                }
              </Col>
              <Col className="d-none d-xl-block">
                <h6 className="m-0">
                  <small className="font-weight-bold" />
                </h6>
                <p />
              </Col>
            </Row>
            <Row>
              <Col>
                {specialPlanBadgeText && (
                  <Badge variant="warning" className="my-2">
                    {specialPlanBadgeText}
                  </Badge>
                )}
              </Col>
            </Row>
            {isDevMode && (
              <Row>
                <AdvancedEditingBlock isEditing={isEditing} isSaving={isSaving} />
              </Row>
            )}
            <hr />
            {!showAllHistory && (
              <>
                <div className="d-flex flex-wrap justify-content-between">
                  <h4>Subscription details</h4>
                  {!downsell_discount_is_available ? (
                    <Badge variant="success" className="h-100 mt-3 mt-lg-0 mr-0 mr-xl-3 mb-2 mb-xl-0">
                      Downsell has been applied to this subscription
                    </Badge>
                  ) : null}
                </div>
                <SubscriptionDetailsTable
                  initialSubscription={initialSubscription}
                  isEditing={isEditing}
                  isSaving={isSaving}
                  setIsEditing={setIsEditing}
                  setInitialSubscription={setInitialSubscription}
                  saveRow={saveRow}
                  showFooter
                  className="mb-4"
                  country={shippingAddress.country}
                />
                {pendingOrder.length ? (
                  <>
                    <hr />
                    <OrderDetailsTable
                      order={pendingOrder}
                      currency={subscription.currency}
                      shop={history[0]?.shop_name}
                      header={
                        <>
                          <h4 className="m-0">Upcoming order #{pendingOrder[0].order_id}</h4>
                          <p className="small m-0 ml-3">
                            {getDateAtTimeFormat(billing?.nextBillingDate) || getDateAtTimeFormatString(signupDate)}
                          </p>
                        </>
                      }
                    />
                  </>
                ) : (
                  <div>No upcoming orders</div>
                )}
                <hr />
              </>
            )}
            {isLoadingSubOrders ? (
              <Spinner animation="border" role="status">
                <span className="sr-only">Loading...</span>
              </Spinner>
            ) : history.length > 0 ? (
              <OrderHistory
                history={history}
                currency={subscription.currency}
                showAllHistory={showAllHistory}
                toggleShowAllHistory={toggleShowAllHistory}
              />
            ) : (
              <div>No Order History</div>
            )}
          </Col>
          <Col
            className="d-flex flex-column bg-gray-light h-100 p-5 flex-grow-0"
            style={{ minWidth: 275 }}
          >
            <CustomerInfoPane
              customer={subscription.customer}
              address={subscription}
              isEditing={isEditing}
              isSaving={isSaving}
            />
          </Col>
        </Row>
      </Container>
      <ExpireModal show={showExpireModal} onHide={onHideModal} subscriptionId={subscription} />
    </>
  );
};
